import * as React from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Text,
  Title3,
  Spinner,
  makeStyles,
  tokens,
  Card,
  Button,
  Badge,
} from "@fluentui/react-components";
import { Clock16Regular, Person16Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { getStatusItemsBySpreadsheet, StatusItemDto } from "../../services/status-items.service";
import { getDiscussionsBySpreadsheet, DiscussionDto } from "../../services/discussions.service";
import { getReviewInfo, SpreadsheetReviewInfoResponse } from "../../services/spreadsheets.service";
import { StatusStatistics } from "../workflow-status/components/StatusStatistics";
import DiscussionStatistics from "../discussions/components/DiscussionStatistics";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { getStatusItemColor } from "../../utils/statusItemHelpers";
import { getStatusGroup } from "../../utils/discussionStatusHelpers";
import { UserRole } from "../../types/UserRole";
import { useTypographyStyles } from "../../styles/typography.styles";
import { useFetch } from "../../hooks/useFetch";

interface HomepageProps {
  title: string;
}

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    marginBottom: tokens.spacingVerticalS,
  },
  greeting: {
    marginTop: tokens.spacingVerticalXS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  recentList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  recentItem: {
    padding: tokens.spacingVerticalS,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    transition: "background 0.1s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  recentItemStatus: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  recentItemContent: {
    flex: 1,
    minWidth: 0,
  },
  recentItemName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  recentItemMeta: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  recentItemMetaItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
  },
  viewAllButton: {
    minWidth: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    color: tokens.colorPaletteRedForeground1,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: "center",
  },
  stepper: {
    display: "flex",
    alignItems: "flex-start",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  stepNode: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXXS,
  },
  stepCircle: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  stepLine: {
    flex: 1,
    height: "2px",
    marginTop: "9px",
    minWidth: "16px",
    position: "relative",
    overflow: "hidden",
  },
  stepLineFill: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
  },
});

// Get time-based greeting
const getTimeBasedGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
};

// Get role-specific blurb based on status counts
const getRoleBlurb = (
  role: UserRole,
  stats: { open: number; processed: number; reworkRequired: number; qualityReviewed: number }
): string => {
  const formatCount = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

  switch (role) {
    case UserRole.Preparer: {
      const parts: string[] = [];
      if (stats.open > 0) parts.push(formatCount(stats.open, "item", "items") + " outstanding");
      if (stats.reworkRequired > 0) parts.push(formatCount(stats.reworkRequired, "item", "items") + " requiring rework");
      if (stats.processed > 0) parts.push(formatCount(stats.processed, "item", "items") + " submitted for review");
      return parts.length > 0 ? "You have " + parts.join(", ") + "." : "All items are up to date.";
    }
    case UserRole.Reviewer: {
      const parts: { count: number; text: string }[] = [];
      if (stats.processed > 0) parts.push({ count: stats.processed, text: formatCount(stats.processed, "item", "items") + " awaiting your review" });
      if (stats.qualityReviewed > 0) parts.push({ count: stats.qualityReviewed, text: formatCount(stats.qualityReviewed, "item", "items") + " awaiting partner review" });
      // Items not yet ready for review (still with preparer)
      const inProgress = stats.open + stats.reworkRequired;
      if (inProgress > 0) parts.push({ count: inProgress, text: formatCount(inProgress, "item", "items") + " still in progress" });
      if (parts.length === 0) return "All items have been reviewed.";
      const verb = parts[0].count === 1 ? "There is " : "There are ";
      return verb + parts.map(p => p.text).join(", ") + ".";
    }
    case UserRole.Partner: {
      const parts: { count: number; text: string }[] = [];
      if (stats.qualityReviewed > 0) parts.push({ count: stats.qualityReviewed, text: formatCount(stats.qualityReviewed, "item", "items") + " awaiting your review" });
      // Items not yet ready for partner review
      const inProgress = stats.open + stats.processed + stats.reworkRequired;
      if (inProgress > 0) parts.push({ count: inProgress, text: formatCount(inProgress, "item", "items") + " still in progress" });
      if (parts.length === 0) return "All items have been reviewed.";
      const verb = parts[0].count === 1 ? "There is " : "There are ";
      return verb + parts.map(p => p.text).join(", ") + ".";
    }
    default:
      return "Welcome to Workflow Intelligence.";
  }
};

/**
 * Dashboard view — key counts, recent activity, role-tailored greeting,
 * and the workbook's review status banner.
 */
const Homepage: React.FC<HomepageProps> = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();
  const { spreadsheetId, userInfo, userRole } = useWorkflowIntelligence();

  // Load all homepage data in parallel
  const { data, isLoading, error } = useFetch(
    async () => {
      const [statusItems, discussions, reviewInfo] = await Promise.all([
        getStatusItemsBySpreadsheet(spreadsheetId!),
        getDiscussionsBySpreadsheet(spreadsheetId!),
        getReviewInfo(spreadsheetId!),
      ]);
      return { statusItems, discussions, reviewInfo };
    },
    [spreadsheetId],
    { enabled: !!spreadsheetId, transformError: getUserFriendlyErrorMessage }
  );
  const statusItems = data?.statusItems ?? [];
  const discussions = data?.discussions ?? [];
  const reviewInfo: SpreadsheetReviewInfoResponse | null = data?.reviewInfo ?? null;

  // Calculate status item statistics — exclude hidden items so the dashboard
  // matches the visible-items view on the Status page.
  const statusItemStats = useMemo(() => {
    const visible = statusItems.filter((item) => !item.isHidden);
    return {
      open: visible.filter((item) => !item.status || item.status === "Open").length,
      processed: visible.filter((item) => item.status === "Processed").length,
      reworkRequired: visible.filter((item) => item.status === "Rework Required").length,
      qualityReviewed: visible.filter((item) => item.status === "Quality Reviewed").length,
      partnerReviewed: visible.filter((item) => item.status === "Partner Reviewed").length,
    };
  }, [statusItems]);

  // Calculate discussion statistics
  const discussionStats = useMemo(() => ({
    openCount: discussions.filter((d) => getStatusGroup(d.status) === "open").length,
    inProgressCount: discussions.filter((d) => getStatusGroup(d.status) === "in_progress").length,
    resolvedCount: discussions.filter((d) => getStatusGroup(d.status) === "closed").length,
  }), [discussions]);

  // Get recently updated status items (sorted by lastUpdated, limit to 5).
  // Excludes hidden items so the dashboard matches the Status page's visible view.
  const recentStatusItems = useMemo(() => {
    return statusItems
      .filter((item) => !item.isHidden)
      .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      .slice(0, 5);
  }, [statusItems]);

  // Format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Handle status item statistic clicks
  const handleStatusClick = (status: string) => {
    navigate(`/workflow-status?status=${encodeURIComponent(status)}`);
  };

  // Handle discussion statistic clicks
  const handleDiscussionStatusClick = (status: string) => {
    navigate(`/discussions?status=${encodeURIComponent(status)}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text>{error}</Text>
        </div>
      </div>
    );
  }

  // Build greeting message
  const greeting = getTimeBasedGreeting();
  const firstName = userInfo?.displayName?.split(" ")[0] || "there";
  const roleBlurb = getRoleBlurb(userRole, statusItemStats);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3>{greeting}, {firstName}</Title3>
        <Text className={`${typography.bodyLarge} ${styles.greeting}`}>{roleBlurb}</Text>
      </div>

      {/* Review Progress */}
      {reviewInfo && (() => {
        const steps = ["Draft", "Manager", "Partner"];
        const currentStep = reviewInfo.status === "Partner Signed" ? 2
          : reviewInfo.status === "Manager Signed" ? 1 : 0;
        return (
          <div className={styles.section}>
            <Text className={typography.sectionTitle}>Review Progress</Text>
            <Card>
              <div className={styles.stepper}>
                {steps.map((label, i) => (
                  <React.Fragment key={label}>
                    <div className={styles.stepNode}>
                      <div
                        className={styles.stepCircle}
                        style={{
                          backgroundColor: i <= currentStep
                            ? tokens.colorPaletteGreenBorder1 : "transparent",
                          border: `2px solid ${i <= currentStep
                            ? tokens.colorPaletteGreenBorder1
                            : tokens.colorNeutralStroke2}`,
                        }}
                      />
                      <Text
                        size={200}
                        weight={i === currentStep ? "semibold" : "regular"}
                        style={{
                          color: i <= currentStep
                            ? tokens.colorNeutralForeground1
                            : tokens.colorNeutralForeground3,
                        }}
                      >
                        {label}
                      </Text>
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        className={styles.stepLine}
                        style={{ backgroundColor: tokens.colorNeutralStroke2 }}
                      >
                        <div
                          className={styles.stepLineFill}
                          style={{
                            width: i < currentStep ? "100%" : i === currentStep ? "50%" : "0%",
                            backgroundColor: tokens.colorPaletteGreenBorder1,
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          </div>
        );
      })()}

      {/* Status Overview */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={typography.sectionTitle}>Status Overview</Text>
          <Button
            appearance="subtle"
            size="small"
            className={styles.viewAllButton}
            onClick={() => navigate("/workflow-status")}
          >
            View all
          </Button>
        </div>
        <StatusStatistics
          openCount={statusItemStats.open}
          processedCount={statusItemStats.processed}
          reworkRequiredCount={statusItemStats.reworkRequired}
          qualityReviewedCount={statusItemStats.qualityReviewed}
          partnerReviewedCount={statusItemStats.partnerReviewed}
          onStatusClick={handleStatusClick}
        />
      </div>

      {/* Discussions Overview */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={typography.sectionTitle}>Discussions Overview</Text>
          <Button
            appearance="subtle"
            size="small"
            className={styles.viewAllButton}
            onClick={() => navigate("/discussions")}
          >
            View all
          </Button>
        </div>
        <DiscussionStatistics
          openCount={discussionStats.openCount}
          inProgressCount={discussionStats.inProgressCount}
          resolvedCount={discussionStats.resolvedCount}
          onStatusClick={handleDiscussionStatusClick}
        />
      </div>

      {/* Recent Activity */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={typography.sectionTitle}>Recent Activity</Text>
          <Button
            appearance="subtle"
            size="small"
            className={styles.viewAllButton}
            onClick={() => navigate("/workflow-status")}
          >
            View all
          </Button>
        </div>
        {recentStatusItems.length > 0 ? (
          <Card>
            <div className={styles.recentList}>
              {recentStatusItems.map((item) => (
                <div
                  key={item.id}
                  className={styles.recentItem}
                  onClick={() => navigate(`/workflow-status?highlight=${item.id}`)}
                >
                  <div
                    className={styles.recentItemStatus}
                    style={{ backgroundColor: getStatusItemColor(item.status || "Open") }}
                  />
                  <div className={styles.recentItemContent}>
                    <Text className={`${typography.listItemPrimary} ${styles.recentItemName}`}>{item.name}</Text>
                    <div className={`${typography.listItemSecondary} ${styles.recentItemMeta}`}>
                      <span className={styles.recentItemMetaItem}>
                        <Clock16Regular />
                        {formatRelativeTime(item.lastUpdated)}
                      </span>
                      {item.preparerName && (
                        <span className={styles.recentItemMetaItem}>
                          <Person16Regular />
                          {item.preparerName}
                        </span>
                      )}
                      {item.status && (
                        <Badge appearance="outline" size="medium" color="informative" className={typography.listItemSecondary}>
                          {item.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card>
            <Text className={`${typography.caption} ${styles.emptyState}`}>No recent activity</Text>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Homepage;
